import { marker as _ } from '@vandres/ngx-translate-extract-marker';
import { AppConfig, TAppColorMode, TAppStyle } from '../../../../shared/schemas/settings';
import { contrast, luminance, parseRgbTriplet } from '../../../../shared/helpers/color';
import { AppConfigStore } from '../../../../state/app-config.repository';

export interface ThemeVariant {
  primary: string;
  accent: string;
  /** Text color on the accent; derived from `accent` when omitted. */
  contrast?: string;
  wallpaper: string;
}

export interface ThemePreset {
  id: string;
  name: string;
  light: ThemeVariant;
  dark: ThemeVariant;
}

export const THEME_MODES = ['light', 'dark'] as const;

/** Id of the tile that stands for the saved theme when it matches no preset. */
export const CURRENT_THEME_ID = 'current';

// Every light/dark variant owns a unique generated wallpaper. The paired scenes
// share a composition and use different lighting for each appearance mode.
export const THEME_PRESETS: readonly ThemePreset[] = [
  {
    id: 'signal',
    name: _('Signal'),
    // Golem: off-white/black foundations, Green 900 for readable light-mode
    // controls and the signature Glowing Green 600 against dark surfaces.
    light: {
      primary: '253,253,253',
      accent: '14,124,71',
      wallpaper: '/assets/bg_signal_golem_light.webp',
    },
    dark: {
      primary: '0,0,0',
      accent: '24,229,131',
      wallpaper: '/assets/bg_signal_golem_dark.webp',
    },
  },
  {
    id: 'canopy',
    name: _('Canopy'),
    light: {
      primary: '211,222,209',
      accent: '61,107,67',
      wallpaper: '/assets/bg_canopy_light.webp',
    },
    dark: { primary: '37,56,47', accent: '157,205,146', wallpaper: '/assets/bg_canopy_dark.webp' },
  },
  {
    id: 'foundry',
    name: _('Foundry'),
    light: {
      primary: '221,208,193',
      accent: '156,81,46',
      wallpaper: '/assets/bg_foundry_light.webp',
    },
    dark: { primary: '52,48,54', accent: '239,169,119', wallpaper: '/assets/bg_foundry_dark.webp' },
  },
  {
    id: 'alpine',
    name: _('Alpine'),
    light: {
      primary: '204,216,228',
      accent: '61,99,156',
      wallpaper: '/assets/bg_alpine_light.webp',
    },
    dark: { primary: '35,50,73', accent: '165,198,242', wallpaper: '/assets/bg_alpine_dark.webp' },
  },
];

export function presetContrast(accent: string): string {
  const rgb = parseRgbTriplet(accent)!;
  const lum = luminance(...rgb);
  return contrast(0, lum) > contrast(1, lum) ? '0,0,0' : '255,255,255';
}

export function presetStyles(preset: ThemePreset): TAppStyle[] {
  const contrastOf = (variant: ThemeVariant) => variant.contrast ?? presetContrast(variant.accent);
  return [
    { id: '--primary-color', light: preset.light.primary, dark: preset.dark.primary },
    { id: '--accent-color', light: preset.light.accent, dark: preset.dark.accent },
    { id: '--contrast-color', light: contrastOf(preset.light), dark: contrastOf(preset.dark) },
  ];
}

export function matchesPreset(config: AppConfig, preset: ThemePreset): boolean {
  const normalize = (color: string | undefined) => color?.replace(/\s/g, '');
  return THEME_MODES.every(
    (mode: TAppColorMode) =>
      config.assets[`${mode}Background`]?.url === preset[mode].wallpaper &&
      presetStyles(preset).every(
        (style) =>
          normalize(config.styles.find((s) => s.id === style.id)?.[mode]) === normalize(style[mode])
      )
  );
}

/** Applies a preset's colors and wallpapers to both themes of the draft. */
export function applyThemePreset(store: AppConfigStore, preset: ThemePreset) {
  for (const style of presetStyles(preset)) store.updateStyle(style);
  for (const mode of THEME_MODES) {
    store.replaceAsset(`${mode}Background`, { url: preset[mode].wallpaper });
  }
}

/**
 * The saved theme as a tile, so a customized instance can return to it after
 * trying presets. `null` when it already matches a preset or is incomplete.
 */
export function savedThemePreset(saved: AppConfig): ThemePreset | null {
  if (THEME_PRESETS.some((preset) => matchesPreset(saved, preset))) return null;
  const variant = (mode: TAppColorMode): ThemeVariant | null => {
    const color = (id: string) => saved.styles.find((style) => style.id === id)?.[mode];
    const primary = color('--primary-color');
    const accent = color('--accent-color');
    const contrast = color('--contrast-color');
    const wallpaper = saved.assets[`${mode}Background`]?.url;
    if (!primary || !accent || !contrast || wallpaper === undefined) return null;
    return { primary, accent, contrast, wallpaper };
  };
  const light = variant('light');
  const dark = variant('dark');
  return light && dark ? { id: CURRENT_THEME_ID, name: _('Current theme'), light, dark } : null;
}
